/**
 * Frozen API contract for Cost+ Checkout Studio.
 *
 * This interface is the single integration seam between UI code and the
 * backing data store. Every method maps 1:1 to an endpoint in API.md.
 *
 * Two implementations:
 *   - LocalStorageClient (Phase -1 — Studio mode, browser-only, default)
 *   - HttpClient         (Phase 5  — Connected mode, real backend, future)
 *
 * Invariants (CLAUDE.md § Studio mode vs Connected mode):
 *   1. UI components import ONLY from this module; no direct fetch/localStorage.
 *   2. Method signatures are stable — components never know which impl is in use.
 *   3. All persisted flow JSON is byte-identical to the future
 *      flow_versions.definition column in Postgres.
 *
 * Changes to this interface are contract-breaking and require a matching
 * update to api/openapi.yaml and the LocalStorage migration framework.
 */

import type {
  Flow,
  FlowDefinition,
  FlowVersion,
  SimulatorResult,
} from "../types/flow";
import type {
  ABAnalyticsResponse,
  AddCostplusProjectInput,
  CostplusProject,
  CreateFlowFixtureInput,
  CreateFlowInput,
  CreateFlowVersionInput,
  FlowFixture,
  FlowTemplateCatalog,
  ForkFromTemplateInput,
  FraudProviderList,
  MediaBannerMetadata,
  PutFraudProviderInput,
  SimulateInput,
  UpdateFlowInput,
} from "../types/api";

export interface SyncFromSourceInput {
  source_type: string;
  source_reference: string;
}

export interface SyncFromSourceResult {
  allowed_countries: string[];
  shipping_methods?: Array<{
    id: string;
    name: string;
    countries?: string[];
    price_cents?: number;
    currency?: string;
  }>;
  default_locale?: string;
  tax_display?: "inclusive" | "exclusive";
  source_metadata?: Record<string, string>;
}

export interface FlowsResource {
  list(): Promise<Flow[]>;
  create(input: CreateFlowInput): Promise<Flow>;
  get(flowId: string): Promise<Flow>;
  update(flowId: string, input: UpdateFlowInput): Promise<Flow>;
  delete(flowId: string): Promise<void>;
  duplicate(
    flowId: string,
    input: { name: string; target_source?: FlowDefinition["nodes"] }
  ): Promise<Flow>;
  /** Pulls commerce defaults (countries, shipping methods, locale, etc.)
   *  from a bound source and returns them for the caller to apply onto a
   *  checkout_page node. Adapter-agnostic: the orchestrator dispatches to
   *  the right adapter (Shopify / future Woo / etc.). */
  syncFromSource(flowId: string, input: SyncFromSourceInput): Promise<SyncFromSourceResult>;
}

/**
 * Per-flow UI sidecar — stores designer-only state that isn't part of the
 * frozen FlowDefinition contract: node positions on the canvas. Lives outside
 * flow_versions.definition so it travels with the flow (not per version).
 */
export interface FlowSidecar {
  positions: Record<string, { x: number; y: number }>;
}

export interface FlowSidecarResource {
  get(flowId: string): Promise<FlowSidecar>;
  save(flowId: string, sidecar: FlowSidecar): Promise<void>;
}

export interface FlowVersionsResource {
  list(flowId: string): Promise<FlowVersion[]>;
  create(flowId: string, input: CreateFlowVersionInput): Promise<FlowVersion>;
  get(flowId: string, versionId: string): Promise<FlowVersion>;
  publish(flowId: string, versionId: string): Promise<FlowVersion>;
  rollback(flowId: string, versionId: string): Promise<FlowVersion>;
}

export interface SimulatorResource {
  run(flowId: string, input: SimulateInput): Promise<SimulatorResult>;
}

export interface FixturesResource {
  list(flowId: string): Promise<FlowFixture[]>;
  create(flowId: string, input: CreateFlowFixtureInput): Promise<FlowFixture>;
  get(flowId: string, fixtureId: string): Promise<FlowFixture>;
  update(
    flowId: string,
    fixtureId: string,
    input: Partial<CreateFlowFixtureInput>
  ): Promise<FlowFixture>;
  delete(flowId: string, fixtureId: string): Promise<void>;
}

export interface TemplatesResource {
  catalog(): Promise<FlowTemplateCatalog>;
  fork(input: ForkFromTemplateInput): Promise<Flow>;
}

export interface MediaResource {
  /**
   * Upload a banner image. In Studio mode, the file is read as a data URL
   * and stored in localStorage; `cdn_url` is the data URL. In Connected
   * mode, the file is POSTed to the backend and returns a real CDN URL.
   */
  uploadBanner(
    file: File,
    input: { alt?: string }
  ): Promise<MediaBannerMetadata>;
}

export interface FraudProvidersResource {
  list(): Promise<FraudProviderList>;
  put(
    provider: "sift" | "signifyd",
    input: PutFraudProviderInput
  ): Promise<void>;
  delete(provider: "sift" | "signifyd"): Promise<void>;
}

export interface CostplusProjectsResource {
  /** List every Cost+ project linked to this merchant (for the designer's dropdown). */
  list(): Promise<CostplusProject[]>;
  /** Add a Cost+ project by providing its per-project API key. The orchestrator
   *  validates the key against the Cost+ gateway before persisting; throws
   *  ApiError with code COSTPLUS_KEY_INVALID on a 401 from Cost+. */
  add(input: AddCostplusProjectInput): Promise<CostplusProject>;
  /** Patch the merchant-facing display name, is_default flag, and optional
   *  per-project API base-URL override. `api_base_url: ""` explicitly
   *  clears a previously-set override back to the orchestrator default. */
  update(
    projectRowId: string,
    input: { project_name?: string; is_default?: boolean; api_base_url?: string }
  ): Promise<CostplusProject>;
  /** Re-validate the stored API key against the Cost+ gateway. On success
   *  bumps `last_validated_at`; throws COSTPLUS_KEY_INVALID on 401. */
  verify(projectRowId: string): Promise<CostplusProject>;
  /** Remove a linked project. Flows that reference it will publish-block until re-bound. */
  remove(projectId: string): Promise<void>;
}

// ShopifyStore describes one of the merchant's connected Shopify stores,
// returned by the backend's /v1/shopify/stores endpoint. The `bound_to`
// field, when present, identifies the flow that has claimed this shop in
// its trigger sources (one-flow-per-shop rule).
export interface ShopifyStoreBinding {
  flow_id: string;
  flow_name: string;
  /** "draft" | "published" — informational; both block reuse. */
  version_state: string;
}

export interface ShopifyStore {
  shop_domain: string;
  scopes: string;
  installed_at: string;
  uninstalled_at?: string;
  active: boolean;
  bound_to?: ShopifyStoreBinding;
}

// Per-merchant connect inputs/outputs (W.2 pivot, 2026-04-28).
export interface ShopifyConnectInput {
  shop_domain: string;
  client_id: string;
  client_secret: string;
  /** Where the merchant should land after OAuth completes. */
  return_to?: string;
  /** Optional override of the granted scopes — defaults to orchestrator's
   *  required set when omitted. Studio wizard sends the empty string. */
  scopes?: string;
}

export interface ShopifyConnectResult {
  /** Shopify's `/admin/oauth/authorize?...` URL — built using the
   *  merchant's own `client_id`. The wizard `window.location.assign`s here. */
  install_url: string;
}

export interface ShopifyResource {
  listStores(): Promise<ShopifyStore[]>;
  /**
   * Returns the full authorize URL that the designer should send the
   * merchant's browser to (either by `window.location.assign` or by
   * opening a new tab). Studio mode returns a placeholder anchor that
   * shows a "Connect Shopify requires Connected mode" toast.
   *
   * @deprecated Legacy global-credential install path. New per-merchant
   * installs use `connect()` (W.2 pivot, 2026-04-28). Kept while
   * funnel-store-test remains on the legacy path.
   */
  installURL(shopDomain: string, returnTo?: string): string;
  /**
   * Per-merchant connect (W.2 pivot). Submits the merchant's own Dev
   * Dashboard custom-app `client_id` + `client_secret`; orchestrator
   * encrypts, persists pending row, returns Shopify's authorize URL
   * built with the merchant's client_id. The Studio wizard then
   * `window.location.assign`s to the returned URL.
   */
  connect(input: ShopifyConnectInput): Promise<ShopifyConnectResult>;
  /** Explicit "Disconnect" — same effect as the app/uninstalled webhook
   *  (zeros the access token, orphans bound flows, flushes live sessions). */
  disconnectStore(shopDomain: string): Promise<void>;
  /** Pre-publish reservation. Resolves on free; throws ApiError(409,
   *  TRIGGER_CONFLICT) with details.conflicting_flow_id when the shop is
   *  already used by another flow. Pure validation hook — doesn't mutate
   *  server state (the binding lives in the flow JSON itself). */
  claimStore(shopDomain: string, flowId: string): Promise<void>;
  /** Re-sync delivery profiles + automatic discounts + product catalog
   *  for this shop. Returns 202; poll syncProgress for live status. */
  resyncStore(shopDomain: string): Promise<void>;
  /** Read current sync progress (0-100%, stage label, done flag). */
  syncProgress(shopDomain: string): Promise<SyncProgress>;
}

export interface SyncProgress {
  key?: string;
  stage?: string;
  stage_label?: string;
  percent: number;
  started_at?: string;
  updated_at?: string;
  completed_at?: string;
  error?: string;
  done: boolean;
}

export interface AnalyticsResource {
  abTestNode(
    flowId: string,
    nodeId: string,
    range: { from: string; to: string; currency?: string }
  ): Promise<ABAnalyticsResponse>;
}

// Auth: returns the current merchant (from cookie) or null if not
// signed in. Connected mode calls the backend `/v1/auth/me`; Studio
// mode returns a synthetic "studio-local-merchant" so UI code can
// render without special-casing the absence of auth.
export interface AuthResource {
  me(): Promise<{ id: string; email: string; display_name: string | null } | null>;
  signin(input: { email: string; password: string }): Promise<void>;
  signup(input: { email: string; password: string; display_name?: string }): Promise<void>;
  signout(): Promise<void>;
}

export interface ApiClient {
  readonly mode: "studio" | "connected";
  readonly auth: AuthResource;
  readonly flows: FlowsResource;
  readonly flowSidecar: FlowSidecarResource;
  readonly versions: FlowVersionsResource;
  readonly simulator: SimulatorResource;
  readonly fixtures: FixturesResource;
  readonly templates: TemplatesResource;
  readonly media: MediaResource;
  readonly fraudProviders: FraudProvidersResource;
  readonly costplusProjects: CostplusProjectsResource;
  readonly analytics: AnalyticsResource;
  readonly shopify: ShopifyResource;
}

/**
 * Resolves which client implementation to use.
 * Studio mode is the default; Connected mode ships in Phase 5.
 */
let _instance: ApiClient | null = null;

export function setApiClient(client: ApiClient): void {
  _instance = client;
}

export function getApiClient(): ApiClient {
  if (!_instance) {
    throw new Error(
      "apiClient not initialized — call setApiClient() from src/main.tsx before rendering",
    );
  }
  return _instance;
}
